import { useState, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth, useScrollToTopOnRouteChange, UserRole } from '@startus/lib';
import { setSentryUser, clearSentryUser } from '@startus/lib';
import { AuthLayout, Header, Footer, LanguageSwitcher } from '@startus/ui';
import Dashboard from './pages/dashboard/Dashboard';
import StartupsDashboard from './pages/StartupsDashboard';
import LoginPage from './pages/LoginPage';
import UsersPage from './pages/UsersPage';
import { ApplicationsPage } from './pages/applications';
import { OpportunitiesPage } from './pages/opportunities';
import ReferenceCodesPage from './pages/ReferenceCodesPage';
import InvestorsPage from './pages/InvestorsPage';
import AiUsagePage from './pages/ai-usage/AiUsagePage';
import HealthPage from './pages/health/HealthPage';
import AdminSidebar from './components/layout/AdminSidebar';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { CookiePolicyPage } from './pages/CookiePolicyPage';

// Syncs Sentry user identity with auth state — must be inside AuthProvider
function AnalyticsIdentifier() {
  const { user } = useAuth();
  useEffect(() => {
    if (user) {
      setSentryUser(user.id);
    } else {
      clearSentryUser();
    }
  }, [user]);
  return null;
}

// Redirects unauthenticated users to the root (login) page. Also catches a valid
// but wrong-typed account (e.g. a startup USER or Hub INVESTOR_HUB account staying
// logged in from another app) — that token is real, just not for this app, so it
// forces a logout instead of rendering a broken admin panel for it (OPA-352).
function RequireAuth() {
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const wrongAccountType = isAuthenticated && !!user && user.role !== UserRole.ADMIN;

  useEffect(() => {
    if (wrongAccountType) {
      logout();
    }
  }, [wrongAccountType, logout]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  return isAuthenticated && !wrongAccountType ? <Outlet /> : <Navigate to="/" replace />;
}

// Shared layout for all authenticated admin pages
function AdminLayout() {
  const { logout } = useAuth();
  const { t } = useTranslation('common');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  useScrollToTopOnRouteChange(mainRef);

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <Header
        menuToggle={
          <button
            type="button"
            className="btn btn-ghost btn-square"
            onClick={() => setSidebarOpen(true)}
            aria-label={t('admin:menu.open')}
            aria-expanded={sidebarOpen}
          >
            <svg className="w-6 h-6" fill="none" stroke="white" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        }
        rightContent={
          // Desktop only — on mobile these controls move into the sidebar
          <div className="hidden lg:flex items-center gap-x-3">
            <LanguageSwitcher />
            <button
              type="button"
              onClick={logout}
              className="btn btn-ghost btn-sm !text-white/70 hover:!text-white"
              aria-label={t('auth.logout')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
            </button>
          </div>
        }
      />

      <div className="flex flex-1 min-h-0">
        <AdminSidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onLogout={logout}
        />

        <main ref={mainRef} className="flex-1 overflow-y-auto min-h-0 flex-col">
          <AuthLayout showLogo={false}>
            <div className="w-full">
              <Outlet />
            </div>
          </AuthLayout>
          <Footer variant="admin" />
        </main>
      </div>
    </div>
  );
}

// Handles unauthenticated state — shows login, redirects to dashboard if authenticated as admin.
// Only an admin session may be redirected: a non-admin one (e.g. the hub user's cookie, shared
// across apps) would bounce off RequireAuth — which logs it out and sends it back here — and
// loop until the logout landed (BUG-ADMIN-1). It sees the login page instead.
function AppContent() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  if (isAuthenticated && user?.role === UserRole.ADMIN) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return <LoginPage onLoginSuccess={() => navigate('/admin/dashboard', { replace: true })} />;
}

function App() {
  return (
    <AuthProvider>
      <AnalyticsIdentifier />
      <Routes>
        {/* Public legal pages */}
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/cookies" element={<CookiePolicyPage />} />

        {/* Protected admin pages */}
        <Route element={<RequireAuth />}>
          <Route element={<AdminLayout />}>
            <Route path="/admin/dashboard" element={<Dashboard />} />
            <Route path="/admin/open-calls" element={<OpportunitiesPage />} />
            <Route path="/admin/startups" element={<StartupsDashboard />} />
            <Route path="/admin/applications" element={<ApplicationsPage />} />
            <Route path="/admin/users" element={<UsersPage />} />
            <Route path="/admin/investors" element={<InvestorsPage />} />
            <Route path="/admin/ai-usage" element={<AiUsagePage />} />
            <Route path="/admin/health" element={<HealthPage />} />
            <Route path="/admin/reference-codes" element={<ReferenceCodesPage />} />
          </Route>
        </Route>

        {/* Auth / catch-all — shows login or redirects to dashboard */}
        <Route path="*" element={<AppContent />} />
      </Routes>
    </AuthProvider>
  );
}

export default App;
